<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  response: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainResponses/getUIFlags'),
};

const initialState = {
  question: '',
  answer: '',
};

const state = reactive({ ...initialState });

const validationRules = {
  question: { required, minLength: minLength(1) },
  answer: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.RESPONSES.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  question: getErrorMessage('question', 'QUESTION'),
  answer: getErrorMessage('answer', 'ANSWER'),
}));

const handleCancel = () => emit('cancel');

const prepareDocumentDetails = () => ({
  question: state.question,
  answer: state.answer,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareDocumentDetails());
};

const updateStateFromResponse = response => {
  if (!response) return;

  const { question, answer } = response;

  Object.assign(state, {
    question,
    answer,
  });
};

watch(
  () => props.response,
  newResponse => {
    if (props.mode === 'edit' && newResponse) {
      updateStateFromResponse(newResponse);
    }
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4 h-full flex-1 min-h-0" @submit.prevent="handleSubmit">
    <Input
      v-model="state.question"
      :label="t('CAPTAIN.RESPONSES.FORM.QUESTION.LABEL')"
      :placeholder="t('CAPTAIN.RESPONSES.FORM.QUESTION.PLACEHOLDER')"
      :message="formErrors.question"
      :message-type="formErrors.question ? 'error' : 'info'"
    />
    <div class="flex-1 min-h-0 flex flex-col">
      <Editor
        v-model="state.answer"
        channel-type="Context::CaptainResponse"
        :label="t('CAPTAIN.RESPONSES.FORM.ANSWER.LABEL')"
        :placeholder="t('CAPTAIN.RESPONSES.FORM.ANSWER.PLACEHOLDER')"
        :message="formErrors.answer"
        :max-length="10000"
        :message-type="formErrors.answer ? 'error' : 'info'"
        class="h-full flex-1 min-h-0 captain-response-editor"
      />
    </div>
    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>

<style scoped>
/*
 * FAQ Editor — Complete flex chain for vertical scrolling.
 *
 * The chain from editor-wrapper → ProseMirror has 3 unstyled intermediate
 * divs (WootEditor root .relative, ProseMirror mount div, .ProseMirror-menubar-wrapper)
 * that break flex propagation. Without flex:1 at each link, the editor content
 * collapses to the global max-h-[7.5rem] (120px) regardless of the dialog's 80vh height.
 *
 * We force every link to participate in column flex so the ProseMirror content
 * area fills all available vertical space and scrolls internally.
 */

/* 1. editor-wrapper fills the form's flex-1 container */
.captain-response-editor :deep(.editor-wrapper) {
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
}

/* 2. WootEditor root (.relative.w-full) fills editor-wrapper */
.captain-response-editor :deep(.editor-wrapper > .relative) {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 3. ProseMirror mount div (ref="editor") fills .relative, becomes flex container */
.captain-response-editor :deep(.editor-wrapper .relative > div) {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 4. ProseMirror-menubar-wrapper fills its parent */
.captain-response-editor :deep(.ProseMirror-menubar-wrapper) {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 5. ProseMirror content area: fill remaining space, scroll internally.
 *    min-height is set large enough to claim most of the dialog's vertical
 *    space. The slot wrapper (overflow-y-auto) handles the rare case where
 *    even this isn't enough. */
.captain-response-editor :deep(.ProseMirror-woot-style) {
  flex: 1 1 0%;
  min-height: 200px;
  max-height: none !important;
  overflow-y: auto;
}
</style>
